// A milestone's moment (AWARDS.md "Milestones", PLAN-2026-10-29-milestones-as-
// they-happen §2, scenario H7): a team the person runs reached a tier when its
// week settled, and the card says so the next time the site is open.
//
// THE ACHIEVEMENT CARD'S SLOT AND MANNERS (`AchievementMoment.tsx`): it slides
// in with the badge popping into its tier's metal, plays the unlock sound when
// sounds are on, opens the team's trophy room when tapped, and goes after a
// few seconds (not while a pointer or focus is on it). It never covers a pick
// on the clock or a sheet, and never beside the achievement card:
// `Moments.tsx` gives the screen to one at a time, an achievement's first.
//
// TWO OR MORE AT ONCE ARE ONE CARD ("3 milestones"), never a stack. They open
// the first one's trophy room, where every badge is.
//
// ONCE ON ANY DEVICE: shown here, they are marked seen.

import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AwardTier, type MilestoneMoment as Moment } from '@fsp/domain';
import { momentsClear, type MomentTurn } from '../data/achievements';
import { markMilestonesSeen, takeMilestones, usePendingMilestones } from '../data/milestones';
import { useAtLeast } from '../data/detail';
import { useSession } from '../data/session';
import { useDelight } from '../delight/useDelight';
import { AwardGlyph } from '../kit/Icon';
import { IconButton } from '../kit/ui';
import { TIER_LABEL } from '../screens/shared/AwardKit';
import { useTier } from './useTier';
import '../styles/screens/achievements.css';

const SHOW_MS = 6500;
const LOOK_EVERY_MS = 700;

/** What the card says for what it holds. */
function wordsFor(
  batch: readonly Moment[],
  counts: boolean,
): { eyebrow: string; name: string; line: string } {
  const [first] = batch;
  if (batch.length === 1 && first !== undefined)
    return {
      eyebrow: `Milestone unlocked · ${TIER_LABEL[first.tier]}`,
      name: first.name,
      line: first.line,
    };
  const names = batch.slice(0, 3).map((m) => m.name);
  const listed =
    batch.length > 3
      ? `${names.join(', ')} and more`
      : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
  // SIMPLE REMOVES THE NUMBER AND ADDS NO WORD (DETAIL_LEVELS.md): the
  // count goes, rather than becoming "some".
  return {
    eyebrow: 'Milestones unlocked',
    name: counts ? `${batch.length} milestones` : 'Milestones',
    line: listed,
  };
}

export function MilestoneMoment({ mayShow, onUp }: MomentTurn) {
  const { session } = useSession();
  const userId = session?.userId ?? null;
  const pending = usePendingMilestones(userId);
  const [showing, setShowing] = useState<Moment[] | null>(null);
  const [held, setHeld] = useState(false);
  const counts = useAtLeast('standard');
  const tier = useTier();
  const navigate = useNavigate();
  const { play } = useDelight();
  const playRef = useRef(play);
  playRef.current = play;
  const close = useCallback(() => {
    onUp(false);
    setShowing(null);
  }, [onUp]);

  // WAIT FOR A CLEAR SCREEN, then show everything that has gathered.
  useEffect(() => {
    if (userId === null || showing !== null || pending.length === 0) return;
    const tryShow = (): boolean => {
      if (!momentsClear() || !mayShow()) return false;
      const batch = takeMilestones(userId);
      if (batch.length === 0) return true;
      onUp(true);
      setShowing(batch);
      playRef.current('unlock');
      void markMilestonesSeen(batch.map((m) => m.id));
      return true;
    };
    if (tryShow()) return;
    const timer = window.setInterval(() => {
      if (tryShow()) window.clearInterval(timer);
    }, LOOK_EVERY_MS);
    return () => window.clearInterval(timer);
  }, [mayShow, onUp, userId, pending, showing]);

  // A card that goes takes its hold with it (AchievementMoment.tsx).
  useEffect(() => {
    if (showing === null) setHeld(false);
  }, [showing]);

  // GONE AFTER A FEW SECONDS, unless a pointer or focus is on it.
  useEffect(() => {
    if (showing === null || held) return;
    const timer = window.setTimeout(close, SHOW_MS);
    return () => window.clearTimeout(timer);
  }, [showing, held, close]);

  // Signed out mid-card: it was theirs, not the next person's.
  useEffect(() => {
    if (userId === null) close();
  }, [userId, close]);

  if (showing === null || showing.length === 0) return null;
  const first = showing[0]!;
  const words = wordsFor(showing, counts);
  const to = `/l/${encodeURIComponent(first.league.id)}/awards?tab=room&team=${encodeURIComponent(first.teamId)}`;
  // The card wears the highest tier's metal.
  const rank = (m: Moment) => AwardTier.options.indexOf(m.tier);
  const top = showing.reduce((best, m) => (rank(m) > rank(best) ? m : best), first);

  return (
    <div
      className="fsp-moment fsp-moment-milestone"
      data-metal={top.tier}
      data-tier={tier}
      role="status"
      aria-live="polite"
      onPointerEnter={() => setHeld(true)}
      onPointerLeave={() => setHeld(false)}
      onFocus={() => setHeld(true)}
      onBlur={() => setHeld(false)}
    >
      <button
        type="button"
        className="fsp-moment-open"
        onClick={() => {
          close();
          navigate(to);
        }}
      >
        <span className="fsp-ach-glyph">
          <AwardGlyph icon={showing.length === 1 ? first.icon : 'medal'} size={24} />
        </span>
        <span className="fsp-grow">
          <span className="fsp-moment-eyebrow">{words.eyebrow}</span>
          <span className="fsp-moment-name">{words.name}</span>
          <span className="fsp-moment-line">
            {showing.length === 1 ? `${first.league.name} · ${words.line}` : words.line}
          </span>
        </span>
      </button>
      <IconButton icon="close" label="Dismiss" size="sm" variant="quiet" onClick={() => close()} />
    </div>
  );
}
